import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { getPageKey } from "./pageKeys";

// Motore centrale di DF Academy (Smart Help e AI Guided Learning). Ogni
// pagina "eredita" automaticamente guida, videoguida e tour perché tutte
// leggono da qui in base al page_key della rotta corrente — nessuna pagina
// deve importare o duplicare nulla di suo per avere i tre pulsanti.
const AcademyCtx = createContext(null);

export function AcademyProvider({ children }) {
  const location = useLocation();
  const pageKey = useMemo(() => getPageKey(location.pathname), [location.pathname]);
  const cache = useRef(new Map());
  const [content, setContent] = useState(null); // null = nessuna guida per questa pagina
  const [loading, setLoading] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [tourStep, setTourStep] = useState(-1); // -1 = tour non attivo

  useEffect(() => {
    setGuideOpen(false);
    setVideoOpen(false);
    setTourStep(-1);
    if (cache.current.has(pageKey)) {
      setContent(cache.current.get(pageKey));
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/app/academy/${pageKey}`)
      .then(({ data }) => {
        if (cancelled) return;
        cache.current.set(pageKey, data.item);
        setContent(data.item);
      })
      .catch(() => {
        if (cancelled) return;
        cache.current.set(pageKey, null);
        setContent(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pageKey]);

  const startTour = useCallback(() => {
    setAssistantOpen(false);
    setTourStep(0);
  }, []);
  const stopTour = useCallback(() => setTourStep(-1), []);
  const nextTourStep = useCallback(() => {
    setTourStep((current) => {
      const steps = content?.tour_steps || [];
      if (current + 1 >= steps.length) return -1;
      return current + 1;
    });
  }, [content]);
  const prevTourStep = useCallback(() => setTourStep((current) => Math.max(0, current - 1)), []);

  const value = useMemo(() => ({
    pageKey, content, loading,
    guideOpen, openGuide: () => setGuideOpen(true), closeGuide: () => setGuideOpen(false),
    videoOpen, openVideo: () => setVideoOpen(true), closeVideo: () => setVideoOpen(false),
    assistantOpen, openAssistant: () => setAssistantOpen(true), closeAssistant: () => setAssistantOpen(false),
    tourStep, startTour, stopTour, nextTourStep, prevTourStep,
  }), [pageKey, content, loading, guideOpen, videoOpen, assistantOpen, tourStep, startTour, stopTour, nextTourStep, prevTourStep]);

  return <AcademyCtx.Provider value={value}>{children}</AcademyCtx.Provider>;
}

export function useAcademy() {
  const ctx = useContext(AcademyCtx);
  if (!ctx) throw new Error("useAcademy va usato dentro <AcademyProvider>");
  return ctx;
}
